import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  DollarSign, 
  Activity, 
  BookOpen, 
  Heart, 
  Leaf, 
  BarChart3, 
  Clock,
  Target,
  AlertCircle,
  CheckCircle2,
  Package,
  Users,
  Zap,
  Calendar,
  Plus,
  Upload,
  FileText,
  Receipt
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const SocialActorDashboard = () => {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Project selection dropdown states
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null); // 'deliverables' or 'reports'

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      toast.error(t('social_actor_dashboard.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'education': return <BookOpen className="h-5 w-5" />;
      case 'care': return <Heart className="h-5 w-5" />;
      case 'environment': return <Leaf className="h-5 w-5" />;
      default: return <Activity className="h-5 w-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'education': return 'bg-blue-100 text-blue-800';
      case 'care': return 'bg-green-100 text-green-800';
      case 'environment': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'executing': return 'bg-green-100 text-green-800';
      case 'planning': return 'bg-yellow-100 text-yellow-800';
      case 'initiating': return 'bg-blue-100 text-blue-800';
      case 'closing': return 'bg-purple-100 text-purple-800';
      case 'on_hold': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleUploadDeliverables = () => {
    // Check if user has projects
    if (!dashboardData?.recent_projects || dashboardData.recent_projects.length === 0) {
      toast.error(t('social_actor_dashboard.no_active_projects_toast'));
      return;
    }
    
    // Always show selection dropdown regardless of project count
    setSelectedAction('deliverables');
    setShowProjectDropdown(true);
  };

  const handleProgressReports = () => {
    // Check if user has projects
    if (!dashboardData?.recent_projects || dashboardData.recent_projects.length === 0) {
      toast.error(t('social_actor_dashboard.no_active_projects_toast'));
      return;
    }
    
    // Always show selection dropdown regardless of project count
    setSelectedAction('reports');
    setShowProjectDropdown(true);
  };

  const handleProjectSelection = (projectId) => {
    if (selectedAction === 'deliverables') {
      window.location.href = `/projects/${projectId}?tab=deliverables`;
    } else if (selectedAction === 'reports') {
      window.location.href = `/projects/${projectId}?tab=reports`;
    }
    setShowProjectDropdown(false);
    setSelectedAction(null);
  };

  const closeProjectDropdown = () => {
    setShowProjectDropdown(false);
    setSelectedAction(null);
  };

  // File upload and progress report functions removed - now handled in project details

  const StatCard = ({ title, value, icon: Icon, subtitle, color = "blue", urgent = false }) => (
    <Card className={`hover-lift transition-all duration-300 ${urgent ? 'ring-2 ring-orange-200' : ''}`} data-testid={`social-stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-500">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100 ${urgent ? 'animate-pulse' : ''}`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Users className="h-8 w-8 text-green-700" />
            <span>{t('social_actor_dashboard.title')}</span>
          </h1>
          <p className="text-gray-600 mt-1">{t('social_actor_dashboard.subtitle')}</p>
        </div>
        <Link to="/projects">
          <Button className="bg-gradient-to-r from-green-600 to-emerald-600">
            <Plus className="h-4 w-4 mr-2" />
            {t('social_actor_dashboard.new_project')}
          </Button>
        </Link>
      </div>

      {/* Project Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('social_actor_dashboard.stat_my_projects')}
          value={dashboardData?.total_projects || 0}
          icon={Activity}
          subtitle={t('social_actor_dashboard.stat_my_projects_subtitle')}
          color="blue"
        />
        <StatCard
          title={t('social_actor_dashboard.stat_active_projects')}
          value={dashboardData?.active_projects || 0}
          icon={TrendingUp}
          subtitle={t('social_actor_dashboard.stat_active_projects_subtitle')}
          color="green"
        />
        <StatCard
          title={t('social_actor_dashboard.stat_project_budget')}
          value={formatCurrency(dashboardData?.total_budget || 0)}
          icon={DollarSign}
          subtitle={t('social_actor_dashboard.stat_project_budget_subtitle')}
          color="purple"
        />
        <StatCard
          title={t('social_actor_dashboard.stat_pending_deliverables')}
          value={dashboardData?.pending_deliverables || 0}
          icon={Package}
          subtitle={t('social_actor_dashboard.stat_pending_deliverables_subtitle')}
          color="orange"
          urgent={dashboardData?.pending_deliverables > 0}
        />
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-green-700" />
              <span>{t('social_actor_dashboard.budget_utilization')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('social_actor_dashboard.budget_spent')}</span>
                <span className="font-semibold">
                  {dashboardData?.total_budget ? 
                    Math.round((dashboardData.total_spent / dashboardData.total_budget) * 100) : 0}%
                </span>
              </div>
              <Progress 
                value={dashboardData?.total_budget ? 
                  (dashboardData.total_spent / dashboardData.total_budget) * 100 : 0} 
              />
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{t('social_actor_dashboard.spent_label')}: {formatCurrency(dashboardData?.total_spent || 0)}</span>
                <span className="text-gray-500">{t('social_actor_dashboard.budget_label')}: {formatCurrency(dashboardData?.total_budget || 0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Zap className="h-5 w-5 text-blue-600" />
              <span>{t('social_actor_dashboard.project_health')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{dashboardData?.active_projects || 0}</p>
                  <p className="text-xs text-green-700">{t('social_actor_dashboard.on_track')}</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-2xl font-bold text-orange-700">{dashboardData?.pending_deliverables || 0}</p>
                  <p className="text-xs text-orange-700">{t('social_actor_dashboard.need_attention')}</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  {dashboardData?.pending_deliverables > 0 
                    ? t('social_actor_dashboard.deliverables_pending', { count: dashboardData.pending_deliverables })
                    : t('social_actor_dashboard.all_deliverables_up_to_date')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Projects */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-blue-600" />
            <span>{t('social_actor_dashboard.my_projects_title')}</span>
          </CardTitle>
          <Link to="/projects">
            <Button variant="outline" size="sm" data-testid="social-view-all-projects">
              {t('social_actor_dashboard.manage_all_projects')}
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {dashboardData?.recent_projects?.map((project) => (
              <div key={project.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="p-2 rounded-full bg-gray-100">
                    {getCategoryIcon(project.category)}
                  </div>
                  <div>
                    <Link 
                      to={`/projects/${project.id}`}
                      className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                      data-testid={`social-project-link-${project.id}`}
                    >
                      {project.title}
                    </Link>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge className={getCategoryColor(project.category)}>
                        {project.category}
                      </Badge>
                      <Badge className={getStatusColor(project.status)}>
                        {project.status.replace('_', ' ')}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {t('social_actor_dashboard.budget_inline', { amount: formatCurrency(project.budget) })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium">{Math.round(project.progress)}%</span>
                    {project.progress >= 75 ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : project.progress >= 25 ? (
                      <Clock className="h-4 w-4 text-yellow-700" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                  <Progress value={project.progress} className="w-24" />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('social_actor_dashboard.due_label', { date: new Date(project.end_date).toLocaleDateString('en-GB') })}
                  </p>
                </div>
              </div>
            ))}
            {(!dashboardData?.recent_projects || dashboardData.recent_projects.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('social_actor_dashboard.no_projects_message')}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card data-testid="social-quick-actions">
        <CardHeader>
          <CardTitle>{t('social_actor_dashboard.project_actions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/projects">
              <Button className="w-full justify-start h-auto p-4" variant="outline">
                <Activity className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <p className="font-medium">{t('social_actor_dashboard.manage_projects')}</p>
                  <p className="text-sm text-gray-500">{t('social_actor_dashboard.manage_projects_desc')}</p>
                </div>
              </Button>
            </Link>
            <Button 
              className="w-full justify-start h-auto p-4" 
              variant="outline" 
              onClick={handleUploadDeliverables}
              data-testid="upload-deliverables-btn"
            >
              <Upload className="h-5 w-5 mr-3" />
              <div className="text-left">
                <p className="font-medium">{t('social_actor_dashboard.upload_deliverables')}</p>
                <p className="text-sm text-gray-500">{t('social_actor_dashboard.upload_deliverables_desc')}</p>
              </div>
            </Button>
            <Button 
              className="w-full justify-start h-auto p-4" 
              variant="outline" 
              onClick={handleProgressReports}
              data-testid="progress-reports-btn"
            >
              <FileText className="h-5 w-5 mr-3" />
              <div className="text-left">
                <p className="font-medium">{t('social_actor_dashboard.progress_reports')}</p>
                <p className="text-sm text-gray-500">{t('social_actor_dashboard.progress_reports_desc')}</p>
              </div>
            </Button>
            <Link to="/reimbursements">
              <Button className="w-full justify-start h-auto p-4" variant="outline" data-testid="social-action-reimbursements">
                <Receipt className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <p className="font-medium">Reimbursements</p>
                  <p className="text-sm text-gray-500">Submit expense claims</p>
                </div>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Project Selection Dropdown Modal */}
      {showProjectDropdown && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {selectedAction === 'deliverables'
                ? t('social_actor_dashboard.select_project_for_deliverables')
                : t('social_actor_dashboard.select_project_for_reports')}
            </h3>
            
            <div className="space-y-3">
              {dashboardData?.recent_projects && dashboardData.recent_projects.map(project => (
                <button
                  key={project.id}
                  onClick={() => handleProjectSelection(project.id)}
                  className="w-full p-3 text-left border border-gray-300 rounded-md hover:bg-gray-50 hover:border-blue-500 transition-colors"
                >
                  <div className="font-medium text-gray-900">{project.title}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {t('social_actor_dashboard.status_inline_label', { status: project.status })} • {t('social_actor_dashboard.progress_label', { progress: project.progress })}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('social_actor_dashboard.category_label', { category: project.category })}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <Button 
                variant="outline" 
                onClick={closeProjectDropdown}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialActorDashboard;